import {
  MediaImagePathVariant,
  type MediaImagePathVariantType,
  MediaImageSize,
  type MediaImageSizeType,
  type Size,
} from './models.ts';
import { parseDimensions, parseFilename, validate } from './paths.ts';

/**
 * The type for media images.
 */
export const MEDIA_IMAGE_TYPE = 'mediaImages';

/**
 * Represents the path to a media image.
 * The path is in the format of:
 * - mediaImages/{variant}/{size}/{id}{extension}
 * - mediaImages/{variant}/{id}{extension}
 * where:
 * - variant is the variant of the image (original, thumbnail, or a size)
 * - size is the size of the image (width x height)
 * - id is the id of the image
 * - extension is the extension of the image
 * The key is the path to the image in the media bucket.
 * The url is the public URL to the image.
 */
export class MediaImagePath {
  /**
   * The type of the path.
   */
  type = MEDIA_IMAGE_TYPE;

  /**
   * The id of the image.
   */
  id: string;

  /**
   * The variant of the path to the image.
   */
  variant: MediaImageSizeType | MediaImagePathVariantType;

  /**
   * The extension of the image.
   */
  extension: string;

  /**
   * The size of the image. This is only used for size variants.
   */
  size: Size | null;

  /**
   * Creates a new MediaImagePath instance.
   * @param props The properties to create the MediaImagePath instance.
   * @throws Error if the id is invalid.
   */
  constructor(props: {
    id: string;
    variant: MediaImageSizeType | MediaImagePathVariantType;
    extension: string;
    size?: Size;
  }) {
    if (!validate(props.id)) {
      throw new Error('Invalid id');
    }

    this.id = props.id;
    this.extension = props.extension;

    switch (props.variant) {
      case MediaImagePathVariant.Original:
      case MediaImagePathVariant.Thumbnail:
        this.variant = props.variant;
        this.size = null;
        break;
      default:
        if (!Object.values(MediaImageSize).includes(props.variant)) {
          throw new Error('Invalid variant');
        }

        if (!props.size) {
          throw new Error('Size is required');
        }

        this.variant = props.variant;
        this.size = props.size;
    }
  }

  /**
   * Gets the key for the media image path.
   */
  get key(): string {
    if (this.size) {
      return `${this.type}/${this.variant}/${this.size.width}x${this.size.height}/${this.id}${this.extension}`;
    }
    return `${this.type}/${this.variant}/${this.id}${this.extension}`;
  }

  /**
   * Gets the URL for the media image path.
   * @param hostname The hostname to use for the URL
   */
  url(hostname: string): string {
    return `https://${hostname}/${this.key}`;
  }

  /**
   * Creates a MediaImagePath from a key.
   * @param key The key to parse
   * @returns A new MediaImagePath instance
   * @throws Error if the key is invalid
   */
  static fromKey(key: string): MediaImagePath {
    const parts = key.split('/');

    if (parts.length < 3) {
      throw new Error('Invalid key');
    }

    const type = parts[0];
    if (type !== MEDIA_IMAGE_TYPE) {
      throw new Error('Invalid type');
    }

    const variant = parts[1] as MediaImageSizeType | MediaImagePathVariantType;

    // For original and thumbnail variants: mediaImages/{variant}/{id}{extension}
    if (
      variant === MediaImagePathVariant.Original ||
      variant === MediaImagePathVariant.Thumbnail
    ) {
      if (parts.length !== 3 || !parts[2]) {
        throw new Error('Invalid key');
      }

      const parsed = parseFilename(parts[2]);
      if (!parsed) {
        throw new Error('Invalid filename');
      }

      return new MediaImagePath({
        id: parsed.id,
        variant,
        extension: parsed.extension,
      });
    }

    // For size variants: mediaImages/{variant}/{size}/{id}{extension}
    if (parts.length !== 4 || !parts[2] || !parts[3]) {
      throw new Error('Invalid key');
    }

    const size = parseDimensions(parts[2]);
    if (!size) {
      throw new Error('Invalid size');
    }

    const parsed = parseFilename(parts[3]);
    if (!parsed) {
      throw new Error('Invalid filename');
    }

    return new MediaImagePath({
      id: parsed.id,
      variant,
      extension: parsed.extension,
      size,
    });
  }

  /**
   * Creates a MediaImagePath from a URL.
   * @param url The URL to parse
   * @returns A new MediaImagePath instance
   * @throws Error if the URL is invalid
   */
  static fromUrl(url: string): MediaImagePath {
    const urlObj = new URL(url);
    // Remove leading slash
    const key = urlObj.pathname.substring(1);
    return MediaImagePath.fromKey(key);
  }
}
